import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  Typography,
  Alert,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import AddKra from "./AddKra";
import { Icon } from "@iconify/react/dist/iconify.js";
import ImportData from "./ImportData";
import ExportData from "./ExportData";
import { deleteKra, getAllKras } from "../../../redux/features/appraisalSlice";

const KraLibrary = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [value, setValue] = useState("");

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, kralibraries } = useSelector((state) => state.appraisal);

  const allSelected = rows.length > 0 && rows.every((row) => row.selected);
  const anySelected = rows.some((row) => row.selected);

  const handleCheckboxChange = (id) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, selected: !row.selected } : row
      )
    );
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setRows((prev) => prev.map((row) => ({ ...row, selected: checked })));
  };

  const handleDeleteSelected = async () => {
    const selectedIds = rows.filter((row) => row.selected).map((row) => row.id);

    if (selectedIds.length === 0) return;

    try {
      await dispatch(deleteKra(selectedIds)).unwrap();
      enqueueSnackbar(`${selectedIds.length} KRA's deleted`, {
        variant: "success",
      });
      setRows((prev) => prev.filter((row) => !row.selected));
    } catch (error) {
      enqueueSnackbar("Failed to delete selected KRAs", { variant: "error" });
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteKra([id])).unwrap();
      enqueueSnackbar("KRA deleted", { variant: "success" });
      setRows((prev) => prev.filter((row) => row.id !== id));
    } catch (error) {
      enqueueSnackbar("Failed to delete KRA", { variant: "error" });
    }
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      color: theme.palette.common.black,
      fontSize: 14,
      border: "1px solid #ccc",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      border: "1px solid #ccc",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  }));

  const data = {
    search: value || "",
    type: "kra",
  };

  useEffect(() => {
    if (open) {
      dispatch(getAllKras(data));
    }
  }, [open, value]);

  const tableContent = (
    <Box>
      <Box
        mb={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 1,
        }}
      >
        <Button
          variant="contained"
          color="error"
          size="small"
          startIcon={<Delete />}
          disabled={!anySelected}
          onClick={handleDeleteSelected}
        >
          Delete
        </Button>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <AddKra dispatchData={data} />
          <ImportData dispatchData={data} library={"kra"} />
          <ExportData />
        </Stack>
      </Box>
      {kralibraries?.length === 0 && (
        <Alert severity="info">No KRA added yet</Alert>
      )}
      {/* Table */}
      {kralibraries?.length > 0 && (
        <Table
          sx={{
            border: "1px solid #ccc",
            borderCollapse: "collapse",
            marginTop: 2,
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={anySelected && !allSelected}
                  onChange={handleSelectAll}
                />
              </StyledTableCell>
              <StyledTableCell>
                <strong>KRA</strong>
              </StyledTableCell>
              <StyledTableCell>
                <strong>Description</strong>
              </StyledTableCell>
              {/* <StyledTableCell align="center">
              <strong>Actions</strong>
            </StyledTableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row.id}>
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    checked={row.selected}
                    onChange={() => handleCheckboxChange(row.id)}
                  />
                </StyledTableCell>
                <StyledTableCell>{row.kra}</StyledTableCell>
                <StyledTableCell>{row.description}</StyledTableCell>
                <StyledTableCell align="center">
                  <Stack direction="row" spacing={1}>
                    <AddKra
                      edit={true}
                      title={row.kra}
                      kraId={row.id}
                      description={row.description}
                      dispatchData={data}
                    />
                    <Button
                      color="error"
                      size="small"
                      variant="outlined"
                      onClick={() => handleDelete(row.id)}
                    >
                      <Icon icon="mdi:delete" width={18} height={18} />
                    </Button>
                  </Stack>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  useEffect(() => {
    if (kralibraries?.length) {
      const mappedRows = kralibraries.map((item) => ({
        id: item.appraisal_library_id,
        kra: item.appraisal_library_name,
        description: item.appraisal_library_description,
        selected: false,
      }));
      setRows(mappedRows);
    }
  }, [kralibraries]);

  return (
    <GlobalDialog
      title={"KRA Library"}
      open={open}
      setOpen={setOpen}
      fullWidth
      fullScreen
      maxWidth={"lg"}
      closeButton={true}
      popupButton={
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => setOpen(true)}
        >
          KRA Library
        </Button>
      }
      content={<Box>{tableContent}</Box>}
    />
  );
};

export default KraLibrary;
